import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { getThemeLang, setThemeLang } from "src/app/core/utils/util";
import { environment } from "src/environments/environment";
import { BehaviorSubject } from "rxjs";
import { LangService } from "src/app/shared/services/lang.service";
import { Title } from "@angular/platform-browser";
import { apiColletions } from "src/app/core/constants/api_collection";
import { Helper } from "src/app/core/helpers/helper";
import defaultString from "../../../assets/i18n/default-string.json";
import { ApiService } from "src/app/shared/services/api.service";

export class Language {
  code!: string;
  direction!: string;
  label!: string;
  shorthand!: string;
}

@Injectable({ providedIn: "root" })
export class LanguageService {
  LANGUAGE_URL = environment.API_URL;
  defaultLang = environment.defaultLang;

  isSingleLang = false;
  renderer: Renderer2;
  defaultLanguage = getThemeLang();
  supportedLanguages: Language[] = [];
  private languages = new BehaviorSubject<any>(null);
  languagesObservable = this.languages.asObservable();

  constructor(
    private translate: TranslateService,
    private rendererFactory: RendererFactory2,
    private helper: Helper,
    private langService: LangService,
    private titleService: Title,
    private apiService: ApiService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  configLanguages(): Promise<any> {
    return new Promise((resolve) => {
      this.langService.getLanguageList().subscribe((response: any) => {
        if (response) {
          const obj = response.find((list: any) => list.code === localStorage.getItem("theme_lang"));
          if (!obj) {
            setThemeLang(this.defaultLang, "ltr");
            this.setPanelName();
            // window.location.reload()
          }
          response.forEach((_lang: any) => {
            const langDirection = _lang.is_lang_rtl === true ? "rtl" : "ltr";
            const langIndex = this.supportedLanguages?.findIndex((x: any) => x.code === _lang.code);

            if(langIndex === -1){
              this.supportedLanguages.push({ code: _lang.code, direction: langDirection, label: _lang.name, shorthand: _lang.code });
            }
          });
          setTimeout(() => {
            this.languages.next({});
          });
          resolve(true);
        } else {
          this.supportedLanguages.push({ code: this.defaultLang, direction: "ltr", label: "English", shorthand: this.defaultLang });
          this.languages.next({});
          resolve(true);
        }
      });
    });
  }

  async init(): Promise<void> {
    this.translate.setTranslation(this.defaultLang, defaultString);
    const response = await this.configLanguages();

    if (response) {
      await this.setServerLanguage();
    }

    this.translate.setDefaultLang(this.defaultLanguage);
    this.translate.use(this.defaultLanguage);

    this.renderer.removeClass(document.body, "ltr");
    this.renderer.removeClass(document.body, "rtl");

    if (this.translate.currentLang === "ar") {
      this.helper.tooltipPlacementLeft = "right";
      this.helper.tooltipPlacementRight = "left";
      this.renderer.addClass(document.body, "rtl");
    } else {
      this.helper.tooltipPlacementLeft = "left";
      this.helper.tooltipPlacementRight = "right";
      this.renderer.addClass(document.body, "ltr");
    }

    this.helper.APP_NAME = this.translate.instant("app-title.app-name");
  }

  setServerLanguage(): Promise<any> {
    return new Promise((resolve) => {
      const parameters = { isShowErrorToast: false };

      this.apiService.get({ url: apiColletions.strings, parameters: parameters }).subscribe((res: any) => {
        // `shouldMerge: true` keeps any strings defined locally in `default-string.json`
        // that aren't yet in the server's strings collection. Without this flag the
        // server response wipes out every local-only key (raw `label-title.X` appears).
        this.translate.setTranslation(this.helper.cuurentLang, res.data.data, true);
        // The server strings collection overwrites local keys. These labels are
        // product copy: keep them even when the database still says Taxi,
        // Ride Share, Check Cab Status, or Promo Code Placeholder.
        this.translate.setTranslation(this.helper.cuurentLang, {
          "label-title": {
            taxi: "R-Hero Ride",
            "ride-share": "R-Hero Ride",
            "taxi-hailing": "R-Hero Ride",
            "check-cab-status": "Check trip status",
            "promo-code-placeholder": "Promo code"
          },
          menu: {
            "taxi-hailing": "R-Hero Ride",
            taxi: "R-Hero Ride"
          }
        }, true);
        this.setPanelName();
        resolve(true);
      });
    });
  }

  setPanelName(): void {
    setTimeout(() => {
      if (!this.titleService.getTitle().startsWith("R-Hero Dispatch")) {
        this.titleService.setTitle("R-Hero Dispatch – Dispatch");
      }
    }, 100);
  }

  checkForDirectionChange(): void {
    this.renderer.removeClass(document.body, "ltr");
    this.renderer.removeClass(document.body, "rtl");
    this.renderer.addClass(document.body, this.direction);
    this.renderer.setAttribute(document.documentElement, "direction", this.direction);
  }

  set language(lang: string) {
    let language = lang || getThemeLang();

    const isSupportedLanguage = this.supportedLanguages.map((item) => item.code).includes(language);
    if (!isSupportedLanguage) {
      language = this.defaultLanguage;
    }
    this.translate.use(language);
    this.checkForDirectionChange();
    setThemeLang(language, this.direction);
  }

  get language(): string {
    return this.translate.currentLang;
  }

  get languageShorthand(): string {
    const currentLang = this.supportedLanguages.find((item) => item.code === this.translate.currentLang);
    this.setPanelName();
    return currentLang ? currentLang.shorthand : this.defaultLang;
  }

  get direction(): string {
    const currentDirection = this.supportedLanguages.find((item) => item.code === this.translate.currentLang);
    return currentDirection ? currentDirection.direction : "ltr";
  }

  get languageLabel(): string {
    return this.supportedLanguages?.find((item) => item.code === this.translate.currentLang)?.label ?? this.defaultLang;
  }

  get selectedlanguageIndex(): number {
    return this.supportedLanguages.findIndex((item) => item.code === this.translate.currentLang);
  }
}
